/**
 * FP3-044 Phase A — Admin Team Notes tab.
 *
 * Reuses the existing collaboration-notes infrastructure (FP3-031).
 * Admin can author admin/team notes; cannot edit provider-authored
 * therapy notes (those live in db.notes, NOT collaboration_notes).
 * All notes remain attributed by author and role.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";

export default function AdminTeamNotesTab({ studentId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.listCollaborationNotes(studentId);
      setNotes(Array.isArray(r.data?.notes) ? r.data.notes : Array.isArray(r.data) ? r.data : []);
    } catch {
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const post = async () => {
    const text = draft.trim();
    if (!text) return;
    setPosting(true);
    try {
      await api.createCollaborationNote(studentId, { body: text, note_type: "admin_note" });
      setDraft("");
      toast.success("Note added");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not post note");
    } finally {
      setPosting(false);
    }
  };

  return (
    <Card className="p-5 bg-white border-[#E5E3DB]" data-testid="admin-team-notes-tab">
      <p className="text-[13px] font-medium text-[#1F2937] mb-3">Team notes</p>
      <p className="text-[11px] text-[#6B7280] mb-3">
        Notes here are visible to the whole team. Provider therapy notes are
        kept private to each provider's workspace.
      </p>
      <div className="mb-4">
        <textarea
          data-testid="admin-team-note-composer"
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add an admin note for the team…"
          className="w-full text-sm border border-[#d4d2cc] rounded-lg p-2 bg-white"
        />
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={post}
            disabled={posting || !draft.trim()}
            data-testid="admin-team-note-post"
            className="px-3 py-1.5 rounded-md bg-[#1F2937] text-white text-[12px] font-medium hover:bg-black disabled:opacity-60"
          >
            {posting ? "Posting…" : "Post note"}
          </button>
        </div>
      </div>
      {loading ? (
        <p className="text-[12px] text-[#6B7280]">Loading…</p>
      ) : notes.length === 0 ? (
        <p className="text-[12px] text-[#6B7280] italic" data-testid="admin-team-notes-empty">
          No team notes yet.
        </p>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => (
            <div
              key={n.id}
              data-testid={`admin-team-note-${n.id}`}
              className="border border-[#E5E3DB] rounded-lg p-3"
            >
              <div className="flex items-center justify-between mb-1">
                <p className="text-[11px] text-[#1F2937] font-medium">
                  {n.author_name || n.author_email || "Anon"}
                  {n.author_role && (
                    <span className="text-[10px] text-[#6B7280] ml-1.5">
                      · {n.author_role}
                    </span>
                  )}
                </p>
                <p className="text-[10px] text-[#6B7280]">
                  {(n.created_at || "").slice(0, 10)}
                </p>
              </div>
              <p className="text-[12px] text-[#1F2937] whitespace-pre-wrap">
                {n.body}
              </p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
